import { Request } from 'express';
import { InitOptions, LanguageDetectorModule, Services } from 'i18next';
// Lang
const locales = ['it', 'en', 'de', 'fr', 'es'];

const defaultLocale = 'it';

const fallbacks = { 'it-*': 'it', 'de-*': 'de', 'en-*': 'en', 'fr-*': 'fr', 'es-*': 'es' };

const getAcceptedLanguagesFromHeader = (header: string) => {
  const languages = header.split(',');
  const preferences: any = {};
  return languages
    .map(item => {
      const preferenceParts = item.trim().split(';q=');
      if (preferenceParts.length < 2) {
        preferenceParts[1] = '1.0';
      } else {
        const quality = parseFloat(preferenceParts[1]);
        preferenceParts[1] = quality.toString() || '0.0';
      }
      preferences[preferenceParts[0]] = preferenceParts[1];

      return preferenceParts[0];
    })
    .filter(lang => Number(preferences[lang]) > 0)
    .sort((a, b) => Number(preferences[b]) - Number(preferences[a]));
};

const getFallback = (targetLocale: string, fallbacks: any) => {
  fallbacks = fallbacks || {};
  if (fallbacks[targetLocale]) return fallbacks[targetLocale];
  let fallBackLocale: string | null = null;
  for (const key in fallbacks) {
    if (targetLocale.match(new RegExp('^' + key.replace('*', '.*') + '$'))) {
      fallBackLocale = fallbacks[key];
      break;
    }
  }
  return fallBackLocale;
};

const guessLanguage = (request: any) => {
  if (typeof request === 'object') {
    const languageHeader = request?.headers ? request.headers['accept-language'] : undefined;
    const languages: any[] = [];
    const regions: any[] = [];

    // 'accept-language' is the most common source
    if (languageHeader) {
      const acceptedLanguages = getAcceptedLanguagesFromHeader(languageHeader);
      let match;
      let fallbackMatch;
      let fallback;
      for (let i = 0; i < acceptedLanguages.length; i++) {
        const lang = acceptedLanguages[i];
        const lr = lang.split('-', 2);
        const parentLang = lr[0];
        const region = lr[1];

        // Check if we have a configured fallback set for this language.
        const fallbackLang = getFallback(lang, fallbacks);
        if (fallbackLang) {
          fallback = fallbackLang;
          // Fallbacks for languages should be inserted
          // where the original, unsupported language existed.
          const acceptedLanguageIndex = acceptedLanguages.indexOf(lang);
          const fallbackIndex = acceptedLanguages.indexOf(fallback);
          if (fallbackIndex > -1) {
            acceptedLanguages.splice(fallbackIndex, 1);
          }
          acceptedLanguages.splice(acceptedLanguageIndex + 1, 0, fallback);
        }

        // Check if we have a configured fallback set for the parent language of the locale.
        const fallbackParentLang = getFallback(parentLang, fallbacks);
        if (fallbackParentLang) {
          fallback = fallbackParentLang;
          // Fallbacks for a parent language should be inserted
          // to the end of the list, so they're only picked
          // if there is no better match.
          if (acceptedLanguages.indexOf(fallback) < 0) {
            acceptedLanguages.push(fallback);
          }
        }

        if (languages.indexOf(parentLang) < 0) {
          languages.push(parentLang.toLowerCase());
        }
        if (region) {
          regions.push(region.toLowerCase());
        }

        if (!match && locales.indexOf(lang) !== -1) {
          match = lang;
          break;
        }

        if (!fallbackMatch && locales.indexOf(parentLang) !== -1) {
          fallbackMatch = parentLang;
        }
      }

      return match || fallbackMatch || defaultLocale;
    }
    return defaultLocale;
  }

  // last resort: defaultLocale
  return defaultLocale;
};

interface LanguageDetectorInterface {
  name: string;

  lookup: (
    req?: Request,
    res?: Response,
    options?: {
      [name: string]: any;
    },
  ) => string | string[] | undefined;

  cacheUserLanguage?: (req: Request, res: Response, lng: string, options?: object) => void;
}

const sharrylandDetector: (isBrowser: boolean) => LanguageDetectorInterface = isBrowser => ({
  name: 'sharrylandDetector',

  lookup: function (req, _res, options) {
    let locale = defaultLocale;
    if (isBrowser) {
      if (!options) return undefined;
      // detect path
      const language = window.location.pathname.match(/\/([a-zA-Z-]*)/g);
      if (language instanceof Array) {
        if (typeof options.lookupFromPathIndex === 'number') {
          if (typeof language[options.lookupFromPathIndex] !== 'string') {
            return undefined;
          }
          locale = language[options.lookupFromPathIndex].replace('/', '');
        } else {
          locale = language[0].replace('/', '');
        }
      }
      if (!locale && !!options?.appLanguage) {
        locale = options?.appLanguage;
      }
      if (!locale && !!options?.sharryFavouriteLanguage) {
        locale = options?.sharryFavouriteLanguage;
      }
      if (!locale) {
        let browserLang: any = window.navigator.languages ? window.navigator.languages[0] : null;
        browserLang =
          browserLang ||
          window.navigator.language ||
          (window.navigator as any).browserLanguage ||
          (window.navigator as any).userLanguage ||
          '';

        if (browserLang.indexOf('-') !== -1) {
          browserLang = browserLang.split('-')[0];
        }

        if (browserLang.indexOf('_') !== -1) {
          browserLang = browserLang.split('_')[0];
        }

        locale = locales.includes(browserLang) ? browserLang : '';
      }
      if (!locale) {
        locale = defaultLocale;
      }
    } else {
      if (!req) return undefined;
      const firstPart = req.url.substring(0, 3).replace('/', '');
      locale = locales.includes(firstPart) ? firstPart : guessLanguage(req);
    }
    return locale;
  },
});

interface LanguageOptions {
  isBrowser?: boolean;
  order?: string[];
  lookupFromPathIndex?: number;
  lookupQuerystring?: string;
  caches?: string[];
  excludeCacheFor?: string[];
  convertDetectedLanguage?: (l: string) => string;
  sharryFavouriteLanguage?: string;
  appLanguage?: string;
}

const getDefaults = (): {
  order: string[];
  lookupFromPathIndex: number;
  lookupQuerystring: string;
  caches: any[];
  excludeCacheFor: string[];
  convertDetectedLanguage: (l: string) => string;
} => ({
  order: ['sharrylandDetector'],
  lookupFromPathIndex: 0,
  lookupQuerystring: 'lang',
  caches: [],
  excludeCacheFor: ['cimode'],
  convertDetectedLanguage: (l: string) => l,
});

export class LanguageDetector implements LanguageDetectorModule {
  type: 'languageDetector';
  services: Partial<Services> = {};
  options!: Omit<LanguageOptions, 'convertDetectedLanguage'> & { convertDetectedLanguage: (l: string) => string };
  i18nOptions: InitOptions<object> | undefined;
  detectors: { [key: string]: LanguageDetectorInterface };
  constructor(services?: Services, options: LanguageOptions = {}) {
    this.type = 'languageDetector';
    this.detectors = {};

    this.init(services, options);
  }

  init(services?: Services, options: LanguageOptions = {}, i18nOptions: InitOptions = {}) {
    this.services = services || { languageUtils: {} }; // this way the language detector can be used without i18next
    this.options = { ...getDefaults(), ...options };
    this.i18nOptions = i18nOptions;

    this.addDetector(sharrylandDetector(!!options.isBrowser));
  }

  addDetector(detector: LanguageDetectorInterface) {
    this.detectors[detector.name] = detector;
  }

  detect(req?: any, res?: any) {
    if (!this.options.order) return undefined;

    const detectionOrder = this.options.order;

    let detected: string[] = [];
    detectionOrder.forEach(detectorName => {
      if (this.detectors[detectorName]) {
        let lookup = this.detectors[detectorName].lookup(req, res, this.options);
        if (lookup && typeof lookup === 'string') lookup = [lookup];
        if (lookup) detected = detected.concat(lookup);
      }
    });

    detected = detected.map(d => this.options.convertDetectedLanguage(d));
    if (this.services.languageUtils.getBestMatchFromCodes) return detected; // new i18next v19.5.0
    return detected.length > 0 ? detected[0] : undefined; // a little backward compatibility
  }
}
